import type {
  ClientChatSuggestionsConfig,
  ComposerStarter,
  ComposerStartersMode,
} from '@/types/composer';
import type { OnboardingStatus } from '@/hooks/use-onboarding';
import {
  mergeComposerStartersWithOnboarding,
  resolveOnboardingComposer,
} from '@/lib/resolve-onboarding-composer';

const LABEL_MAX = 40;

function truncateLabel(text: string): string {
  const t = text.trim();
  if (t.length <= LABEL_MAX) return t;
  return `${t.slice(0, LABEL_MAX - 1)}…`;
}

function toStarters(messages: string[]): ComposerStarter[] {
  return messages
    .map((m) => m.trim())
    .filter(Boolean)
    .map((message) => ({
      label: truncateLabel(message),
      message,
    }));
}

export function resolveComposerStarters(
  chatSuggestions: ClientChatSuggestionsConfig | undefined,
  mode: ComposerStartersMode,
  focusKey: string | null | undefined,
  starterCount: number,
  onboardingStatus?: OnboardingStatus | null,
): ComposerStarter[] {
  if (mode === 'none' || !chatSuggestions) return [];

  const onboardingComposer = resolveOnboardingComposer(
    onboardingStatus,
    chatSuggestions,
    focusKey,
    starterCount,
  );

  let source: string[] = [];

  if (mode === 'static') {
    source = chatSuggestions.valuePropositions ?? [];
  } else if (mode === 'focus-aware') {
    const jtbd = chatSuggestions.jobsToBeDone ?? {};
    const focus = focusKey?.trim() ?? '';
    const focusList = focus && jtbd[focus]?.length ? jtbd[focus] : undefined;
    source =
      focusList ||
      jtbd.default ||
      chatSuggestions.valuePropositions ||
      chatSuggestions.completionGoals ||
      [];
  }

  const focusStarters = toStarters(source).slice(0, starterCount);
  return mergeComposerStartersWithOnboarding(focusStarters, onboardingComposer);
}
